import { useState, useMemo } from "react";
import { ChevronDown, MoreHorizontal, Plus } from "lucide-react";
import { Button } from "../components/ui/button.tsx";
import { Card } from "../components/ui/card.tsx";
import { useParams } from "react-router-dom";
import { useGetDepartmentById } from "../api/department/departmentQueries";
import { generateDepartmentColor } from '../utils/constants';

const TabButton = ({ selected, label, onClick }) => (
  <button
    onClick={onClick}
    className={`h-10 px-4 ${
      selected 
        ? "text-blue-600 font-medium border-b-2 border-blue-600" 
        : "text-gray-500"
    }`}
  >
    {label}
  </button>
);

export default function DepartmentDetails() {
  const { deptId } = useParams();
  const [activeTab, setActiveTab] = useState("classes");
  const { data: department, isLoading, error } = useGetDepartmentById(deptId);

  // Memoize department color to keep it consistent
  const departmentColor = useMemo(() => {
    if (!department?.deptId) return generateDepartmentColor();
    return generateDepartmentColor();
  }, [department?.deptId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  if (error || !department) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="bg-red-100 text-red-800 p-4 rounded-lg mb-4">
            <p className="font-semibold mb-2">Error loading department data</p>
            <p>{error?.message || 'Department not found'}</p>
          </div>
          <Button 
            onClick={() => window.history.back()}
            variant="secondary"
          >
            Go Back
          </Button>
        </div>
      </div>
    );
  }

  const { bg, text } = departmentColor;

  return (
    <div className="flex flex-col lg:flex-row gap-6 p-6 bg-gray-50 min-h-screen">
      <div className="flex-1">
        {/* Header Card */}
        <Card className="mb-6 rounded-lg overflow-hidden" style={{ borderColor: text }}>
          <div className="p-5">
            <div className="flex items-center gap-2 mb-5">
              <h1 className="text-lg font-semibold">{department?.name}</h1>
              <span className="text-xs text-gray-500">•</span>
              <span className="text-xs text-gray-500">Academic Department</span>
            </div>

            <div className="flex gap-4">
              <div className="bg-gray-50 rounded-lg p-4 flex-1">
                <div className="text-sm text-gray-500 mb-1">No. Of Students</div>
                <div className="flex items-center gap-2">
                  <div className="text-2xl font-semibold">{department?.students?.length || 0}</div>
                  <div className="text-xs text-green-500 flex items-center">
                    +11.01%
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="ml-1">
                      <path d="M7 17l5-5 5 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 flex-1">
                <div className="text-sm text-gray-500 mb-1">Active Staff</div>
                <div className="flex items-center gap-2">
                  <div className="text-2xl font-semibold">{department?.staffs?.length || 0}</div>
                  <div className="text-xs text-green-500 flex items-center">
                    +11.01%
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="ml-1">
                      <path d="M7 17l5-5 5 5" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>

        {/* Navigation Tabs */}
        <div className="flex justify-between items-center mb-4">
          <div className="flex border-b w-full">
            <TabButton
              selected={activeTab === "classes"}
              label="Classes"
              onClick={() => setActiveTab("classes")}
            />
            <TabButton
              selected={activeTab === "schedule"}
              label="Schedule"
              onClick={() => setActiveTab("schedule")}
            />
            <TabButton
              selected={activeTab === "salary"}
              label="Salary"
              onClick={() => setActiveTab("salary")}
            />
            <TabButton
              selected={activeTab === "attendance"}
              label="Attendance"
              onClick={() => setActiveTab("attendance")}
            />
          </div>

          <Button 
            className="bg-purple-600 hover:bg-purple-700 rounded-md"
            onClick={() => {
              // TODO: Implement assign employee functionality
              console.log("Assign employee clicked");
            }}
          >
            <Plus className="h-4 w-4 mr-1.5" /> Assign Employee
          </Button>
        </div>

        {/* Table Header */}
        <div className="grid grid-cols-4 bg-gray-100 p-4 rounded-t-lg text-sm font-medium text-gray-500 uppercase">
          <div>Classes</div>
          <div>Class Attendance</div>
          <div>Grade</div>
          <div>Performance</div>
        </div>

        {/* Table Content */}
        <div className="bg-white rounded-b-lg overflow-hidden border border-gray-200">
          {department?.classes?.map((item, index) => (
            <div key={index} className="grid grid-cols-4 p-4 border-b last:border-b-0">
              <div>
                <div className="font-medium mb-3">{item.name || `Class ${index + 1}`}</div>
                <div className="flex -space-x-2">
                  {item.teachers?.slice(0, 5).map((teacher, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-white overflow-hidden bg-gray-200"
                    >
                      {teacher.avatar ? (
                        <img 
                          src={teacher.avatar} 
                          alt={teacher.name} 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-purple-500 text-white text-xs font-medium">
                          {teacher.name?.charAt(0)}
                        </div>
                      )}
                    </div>
                  ))}
                  {item.teachers?.length > 5 && (
                    <div className="flex items-center justify-center w-8 h-8 rounded-full bg-purple-500 text-white text-xs font-medium border-2 border-white">
                      +{item.teachers.length - 5}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center">
                <svg width="100%" height="60" viewBox="0 0 100 60" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id={`gradient-${index}`} x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor="#a855f7" stopOpacity="0.7" />
                      <stop offset="100%" stopColor="#a855f7" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  <path
                    d="M0,60 L0,35 C10,30 20,45 30,40 C40,35 50,20 60,25 C70,30 80,15 90,20 L100,15 L100,60 Z"
                    fill={`url(#gradient-${index})`}
                  />
                  <path
                    d="M0,35 C10,30 20,45 30,40 C40,35 50,20 60,25 C70,30 80,15 90,20 L100,15"
                    fill="none"
                    stroke="#a855f7"
                    strokeWidth="2"
                  />
                </svg>
              </div>

              <div className="flex items-center justify-center">
                <div className="relative w-16 h-16">
                  <svg viewBox="0 0 36 36" className="w-16 h-16 transform -rotate-90">
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#eee"
                      strokeWidth="3"
                      strokeDasharray="100, 100"
                    />
                    <path
                      d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                      fill="none"
                      stroke="#7dd3ae"
                      strokeWidth="3"
                      strokeDasharray={`${item.grade * 10}, 100`}
                    />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center text-lg font-medium text-green-600">
                    {item.grade || '7.5'}
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <div className="w-3/4">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-purple-500 rounded-full" 
                      style={{ width: `${item.performance || 60}%` }}
                    ></div>
                  </div>
                </div>
                <button 
                  className="p-2 hover:bg-gray-100 rounded-full"
                  onClick={() => {
                    // TODO: Implement class actions menu
                    console.log("Class actions clicked", item);
                  }}
                >
                  <MoreHorizontal className="text-gray-400" />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Right Sidebar */}
      <div className="w-full lg:w-72 bg-white p-6 rounded-lg shadow-sm">
        <div className="mb-10">
          <div className="text-lg font-medium mb-2">Performance</div>
          <div className="text-5xl font-bold text-green-500">
            {department?.performance || 85}%
          </div>
        </div>

        <div className="space-y-6">
          {department?.subjects?.map((item, index) => (
            <div key={index} className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{item.name}</span>
                <span>{item.progress}%</span>
              </div>
              <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-purple-600 rounded-full" 
                  style={{ width: `${item.progress}%` }}
                ></div>
              </div>
            </div>
          ))}

          <div className="flex justify-between items-center mt-10 text-sm">
            <span className="font-medium">All Subjects</span>
            <ChevronDown className="h-4 w-4" />
          </div>
        </div>
      </div>
    </div>
  );
}